/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import RenderBinNode from "App/render/webgl/nodes/render_bin_node";
import WebGLRenderGraph, {ERenderFiler} from "App/render/webgl/render_graph";
import Pass from "App/render/webgl/passes/pass";
import WebGLContext from "App/web_gl_context";
import RenderOptionNode from "App/render/webgl/nodes/render_option_node";
import Factory from "App/render/webgl/nodes/bins/factory";
import {ShadowVolumesBinTypeName} from "App/render/webgl/nodes/bins/type_names";
import IScene from "js_interface/src/scene_interface";

export default
class ShadowVolume extends RenderBinNode {

    public static TypeName: string = ShadowVolumesBinTypeName;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph, null);
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): ShadowVolume {
        return new ShadowVolume(aSceneAPI, aRenderGraph);
    }

    public setUniforms(aRenderPass: Pass) {
        let view = aRenderPass.view;
        let shader: any = aRenderPass.getActiveShaderRenderable();
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        gl.uniform4fv(shader.uniformLocations.u_sun_position, view.getSunLight().getPosition());

        super.setUniforms(aRenderPass);

        // send model view and model view projection matrix (with far plane at infinity) to shader
        gl.uniformMatrix4fv(shader.uniformLocations.u_view_projection, false, view.getCamera().getViewProjectionMatrix());
    }

    public render(aRenderPass: Pass): boolean {
        let gl = (<WebGLContext>this._context).getWebGLRenderingContext();

        gl.depthMask(false);
        gl.stencilFunc(gl.ALWAYS, 0, 0);

        let useZPass = false;
        if (useZPass) {
            // z-pass
            gl.depthFunc(gl.LESS);
            gl.stencilOpSeparate(gl.FRONT, gl.KEEP, gl.KEEP, gl.INCR_WRAP);
            gl.stencilOpSeparate(gl.BACK, gl.KEEP, gl.KEEP, gl.DECR_WRAP);
        } else {
            // z-fail
            gl.depthFunc(gl.LESS);
            gl.stencilOpSeparate(gl.FRONT, gl.KEEP, gl.DECR_WRAP, gl.KEEP);
            gl.stencilOpSeparate(gl.BACK, gl.KEEP, gl.INCR_WRAP, gl.KEEP);
        }

        gl.colorMask(false, false, false, false);

        ////////////////
        // draw sides //
        ////////////////
        aRenderPass.setActiveShaderRenderableIndex(0);
        aRenderPass.bindShader(this._typeName);

        this.setUniforms(aRenderPass);

        aRenderPass.state.setRenderNodesFilters([{
            type: RenderOptionNode.TypeName,
            value: ERenderFiler.isSide
        }
        ]);

        super.render(aRenderPass);

        ///////////////
        // draw caps //
        ///////////////
        aRenderPass.setActiveShaderRenderableIndex(1);
        aRenderPass.bindShader(this._typeName);

        if (!useZPass) {
            this.setUniforms(aRenderPass);

            aRenderPass.state.setRenderNodesFilters([{
                type: RenderOptionNode.TypeName,
                value: ERenderFiler.isCap
            }
            ]);

            super.render(aRenderPass);
        }
        aRenderPass.state.resetRenderNodesFilters();

        gl.enable(gl.DEPTH_TEST);
        gl.depthFunc(gl.LEQUAL);
        gl.depthMask(true);
        gl.colorMask(true, true, true, true);

        aRenderPass.unbindShader();

        // reset the active shader index for the next run of this pass
        aRenderPass.setActiveShaderRenderableIndex(0);

        return true;
    }
}

Factory.register(ShadowVolume);
